import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/icons';
import { startConversationPreview, sendConversationPreview } from '@/data/client';

/* A working conversationIQ on the client's own pages and FAQ, inside tritonIQ,
   before anything is on their website. Same corpus, rules and model as the
   live widget; a preview session never creates a lead or sends an email —
   the note under an answer says what would have happened. */
export function ConversationPreview() {
  const [session, setSession] = useState<{ id: string; persona: string; greeting: string; business: string } | null>(null);
  const [msgs, setMsgs] = useState<{ role: 'user' | 'assistant'; text: string; note?: string; cites?: { title: string; url: string | null }[] }[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length, status]);

  const ensure = async () => {
    if (session) return session;
    const r = await startConversationPreview();
    const s = { id: r.sessionId, persona: r.persona, greeting: r.greeting, business: r.business };
    setSession(s); setMsgs([{ role: 'assistant', text: r.greeting }]);
    return s;
  };
  useEffect(() => { ensure().catch((e) => setErr(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'Preview unavailable.')); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const ask = async (text: string) => {
    const t = text.trim(); if (!t || busy) return;
    setDraft(''); setBusy(true); setStatus('Thinking…'); setErr(null);
    setMsgs((m) => [...m, { role: 'user', text: t }]);
    try {
      const s = await ensure();
      await sendConversationPreview(s.id, t, (ev, d) => {
        if (ev === 'status') setStatus((d as { text: string }).text);
        if (ev === 'answer') {
          const a = d as { text: string; citations: { title: string; url: string | null }[]; capture?: Record<string, string>; wouldCreateLead?: boolean; unanswered: string[] };
          const note = a.wouldCreateLead ? 'Live, this would have sent a lead to your sales inbox and leadIQ.' : a.capture && Object.keys(a.capture).length ? `Noted so far: ${Object.entries(a.capture).map(([k, v]) => `${k} ${v}`).join(', ')}.` : a.unanswered.length ? 'Not in your pages yet — this is the kind of question the FAQ is for.' : undefined;
          setMsgs((m) => [...m, { role: 'assistant', text: a.text, note, cites: a.citations }]);
        }
      });
    } catch (e) { setErr(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'The preview could not answer.'); }
    finally { setBusy(false); setStatus(''); }
  };

  return (
    <div className="cpv">
      <div className="cpv__head">
        <span className="cpv__avatar">{(session?.persona || 'A').charAt(0).toUpperCase()}</span>
        <div><strong>{session?.persona || 'Assistant'}</strong><span>AI assistant for {session?.business || 'your business'} · preview</span></div>
      </div>
      <div className="cpv__log">
        {msgs.map((m, i) => (
          <div key={i} className={`cpv__msg cpv__msg--${m.role}`}>
            <div className="cpv__bubble">{m.text}</div>
            {m.cites && m.cites.length > 0 && <div className="cpv__cites">Source: {m.cites.map((c, j) => <span key={j}>{j > 0 && ' · '}{c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.title}</a> : c.title}</span>)}</div>}
            {m.note && <div className="cpv__note">{m.note}</div>}
          </div>
        ))}
        {status && <div className="cpv__status">{status}</div>}
        {err && <div className="cpv__err">{err}</div>}
        <div ref={endRef} />
      </div>
      <form className="cpv__composer" onSubmit={(e) => { e.preventDefault(); void ask(draft); }}>
        <input className="tr-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Ask as a visitor would…" disabled={busy || !!err} />
        <button type="submit" className="tr-btn tr-btn--primary tr-btn--sm" disabled={busy || !draft.trim()}><Icon name="send" size={14} /></button>
      </form>
      <p className="cpv__disclaimer">Answers come from your website pages and FAQ as they are in tritonIQ today. Before it goes live, Triton sets the persona, the rules and where leads go. Nothing here reaches your website or your inbox.</p>
    </div>
  );
}
